import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocalSearchParams, useRouter } from "expo-router";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import { TurnstileField } from "~/components/TurnstileField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  PasswordField,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { usePasswordValidation } from "~/hooks/usePasswordValidation";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { registerSchema } from "~/utils/form-schemas";
import {
  getDevAuthDefaults,
  isRegistrationEnabled,
} from "~/utils/runtime-config";
import { isTurnstileConfigured } from "~/utils/turnstile";
import { COLLECTIONS_PATH } from "~/utils/onboarding";

type RegisterValues = z.infer<typeof registerSchema>;

export default function RegisterScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ callbackURL?: string }>();
  const { register, registerPending } = useAuth();
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const registrationEnabled = isRegistrationEnabled();
  const turnstileEnabled = isTurnstileConfigured();
  const devDefaults = getDevAuthDefaults();
  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: devDefaults.firstName,
      lastName: devDefaults.lastName,
      email: devDefaults.email,
      country: "NZ",
      password: devDefaults.password,
      confirmPassword: devDefaults.password,
      captchaToken: "",
    },
  });
  const password = form.watch("password");
  const validation = usePasswordValidation(password);
  const destination = normalizeAuthRedirectPath(
    params.callbackURL,
    COLLECTIONS_PATH,
  );
  const loginPath = `/login?callbackURL=${encodeURIComponent(destination)}`;

  const submit = form.handleSubmit(async (values) => {
    if (turnstileEnabled && !values.captchaToken) {
      form.setError("captchaToken", { message: "Complete the spam check." });
      return;
    }
    try {
      const result = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        country: values.country,
        password: values.password,
        callbackUrl: destination,
        captchaToken: turnstileEnabled
          ? values.captchaToken || undefined
          : undefined,
      });
      if (result.signedIn) {
        router.replace(destination as never);
      } else setRegistrationComplete(true);
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error ? error.message : "Registration failed.",
      });
    }
  });

  return (
    <AuthScreen title="Create account">
      {!registrationEnabled ? (
        <AppText>Registrations are not currently open.</AppText>
      ) : registrationComplete ? (
        <Stack gap="sm">
          <AppText weight="semibold">Check your email</AppText>
          <AppText>
            If a confirmation email is needed, follow the link sent to your
            email, then return to sign in and start cataloguing.
          </AppText>
          <AppLink href={loginPath as never}>Back to sign in</AppLink>
        </Stack>
      ) : (
        <Stack>
          <AppText size="xs">
            Free to start: one collection and ten plants, offline on every
            device. Upgrade whenever you outgrow it.
          </AppText>
          {form.formState.errors.root?.message ? (
            <AppText tone="danger" size="xs">
              {form.formState.errors.root.message}
            </AppText>
          ) : null}
          <FormField control={form.control} name="firstName" label="First name">
            {(field) => (
              <TextField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="given-name"
              />
            )}
          </FormField>
          <FormField control={form.control} name="lastName" label="Last name">
            {(field) => (
              <TextField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="family-name"
              />
            )}
          </FormField>
          <FormField control={form.control} name="email" label="Email">
            {(field) => (
              <TextField
                {...field}
                value={String(field.value ?? "")}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                placeholder="you@example.com"
              />
            )}
          </FormField>
          <FormField
            control={form.control}
            name="country"
            label="Country"
            description="Used to suggest sensible defaults for units and dates."
          >
            {(field) => (
              <SearchableSelect
                title="Choose country"
                value={String(field.value ?? "NZ")}
                options={[
                  { label: "New Zealand", value: "NZ" },
                  { label: "Australia", value: "AU" },
                  { label: "United Kingdom", value: "GB" },
                  { label: "United States", value: "US" },
                  { label: "Other", value: "OTHER" },
                ]}
                placeholder="Choose country"
                onValueChange={(value) => {
                  field.onChangeText(value);
                  field.onBlur();
                }}
              />
            )}
          </FormField>
          <FormField control={form.control} name="password" label="Password">
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="Password"
              />
            )}
          </FormField>
          <FormField
            control={form.control}
            name="confirmPassword"
            label="Confirm password"
          >
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="Confirm password"
              />
            )}
          </FormField>
          {validation ? (
            <Stack gap="xs">
              <AppText size="xs">
                Password strength: {validation.strength}
              </AppText>
              {validation.errors.map((message) => (
                <AppText key={message} tone="danger" size="xs">
                  - {message}
                </AppText>
              ))}
            </Stack>
          ) : null}
          {turnstileEnabled ? (
            <TurnstileField
              onToken={(token) =>
                form.setValue("captchaToken", token ?? "", {
                  shouldValidate: true,
                })
              }
            />
          ) : null}
          {form.formState.errors.captchaToken?.message ? (
            <AppText tone="danger" size="xs">
              {form.formState.errors.captchaToken.message}
            </AppText>
          ) : null}
          <Button onPress={() => void submit()} disabled={registerPending}>
            <ButtonText>
              {registerPending ? "Creating account..." : "Create account"}
            </ButtonText>
          </Button>
          <AppLink href={loginPath as never}>
            Already have an account? Sign in
          </AppLink>
        </Stack>
      )}
    </AuthScreen>
  );
}
